import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, FormErrorSummary, Input, Notice } from "../../components/ui";
import { ApiError, authApi } from "../../lib/api";
import { loginSchema, type LoginFormValues } from "../../lib/form-schemas";
import { hasPanelAccess } from "../../lib/permissions";
import { useAuthStore } from "../../store/auth-store";
import { useUiStore } from "../../store/ui-store";

export function LoginPage() {
  const { accessToken, user, setSession, hydrated } = useAuthStore();
  const theme = useUiStore((state) => state.theme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
  }, [theme]);
  const [showPassword, setShowPassword] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<"authenticator" | "sms">("authenticator");
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { identifier: "", password: "", twoFactorCode: "" },
  });

  if (hydrated && accessToken && hasPanelAccess(user)) {
    return <Navigate to="/" replace />;
  }

  const submit = handleSubmit(async (values) => {
    setServerError("");

    if (needsTwoFactor && !values.twoFactorCode?.trim()) {
      setError("twoFactorCode", { type: "manual", message: twoFactorMethod === "sms" ? "کد پیامکی یا کد بازیابی را وارد کنید." : "کد Authenticator یا کد بازیابی را وارد کنید." }, { shouldFocus: true });
      return;
    }

    try {
      const session = await authApi.login(
        values.identifier.trim(),
        values.password,
        true,
        needsTwoFactor ? values.twoFactorCode?.trim() : undefined,
      );

      if (!hasPanelAccess(session.user)) {
        throw new Error("این حساب مجوز دسترسی به پنل مدیریت را ندارد.");
      }

      setSession(session);
      navigate(from, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && ["two_factor_required", "two_factor_sms_required"].includes(caught.problem?.code || "")) {
        const sms = caught.problem?.code === "two_factor_sms_required";
        setTwoFactorMethod(sms ? "sms" : "authenticator");
        setNeedsTwoFactor(true);
        setServerError(sms ? "کد یک‌بارمصرف به شماره موبایل حساب ارسال شد. کد پیامکی یا یکی از کدهای بازیابی را وارد کنید." : "برای تکمیل ورود، کد ۶ رقمی Authenticator یا یکی از کدهای بازیابی را وارد کنید.");
      } else {
        setServerError(caught instanceof Error ? caught.message : "ورود انجام نشد. اطلاعات را بررسی و دوباره تلاش کنید.");
      }
    }
  });

  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <main className="login-page">
        <section className="login-visual" aria-hidden="true">
          <div className="login-visual__overlay" />
          <div className="login-visual__content">
            <img className="login-brand-logo" src="/lia-logo.png" alt="Lia Perfume" />
            <p className="eyebrow">LIA PERFUME</p>
            <h1>مدیریت حرفه‌ای فروشگاه، با تمرکز بر فروش و تجربه مشتری</h1>
            <p>کاتالوگ، سفارش‌ها، محتوا و ارتباط با مشتری در یک فضای منظم، سریع و مناسب کار روزانه مدیریت می‌شوند.</p>
          </div>
        </section>

        <section className="login-form-wrap">
          <form className="login-card" onSubmit={submit} noValidate>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">خوش آمدید</p>
                <h2>{needsTwoFactor ? "تأیید دومرحله‌ای" : "ورود مدیر فروشگاه"}</h2>
                <p>{needsTwoFactor ? "کد امنیتی حساب را برای ادامه وارد کنید." : "اطلاعات حساب مدیریتی خود را وارد کنید."}</p>
              </div>
              <div className="login-card__mark">{needsTwoFactor ? <ShieldCheck /> : <KeyRound />}</div>
            </div>

            {serverError ? <Notice tone="warning">{serverError}</Notice> : null}
            <FormErrorSummary errors={Object.values(errors).map((error) => error?.message)} />

            <Input
              label="موبایل یا ایمیل"
              autoComplete="username"
              placeholder="0912... یا admin@example.com"
              hint="شماره موبایل یا ایمیل ثبت‌شده حساب مدیریت."
              error={errors.identifier?.message}
              {...register("identifier")}
            />

            <div className="password-field">
              <Input
                label="رمز عبور"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                error={errors.password?.message}
                {...register("password")}
              />
              <button
                type="button"
                aria-label={showPassword ? "پنهان کردن رمز" : "نمایش رمز"}
                onClick={() => setShowPassword((current) => !current)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {needsTwoFactor ? (
              <Input
                label="کد تأیید دومرحله‌ای"
                autoComplete="one-time-code"
                dir="ltr"
                inputMode="numeric"
                placeholder="123456"
                hint={twoFactorMethod === "sms" ? "کد یک‌بارمصرف پیامک‌شده یا یکی از کدهای بازیابی." : "کد ۶ رقمی Authenticator یا یکی از کدهای بازیابی یک‌بارمصرف."}
                error={errors.twoFactorCode?.message}
                {...register("twoFactorCode", {
                  setValueAs: (value: string) => value.replace(/[^A-Za-z0-9\s-]/g, ""),
                })}
              />
            ) : null}

            <Button type="submit" busy={isSubmitting}>
              {needsTwoFactor ? "تأیید و ورود" : "ورود به پنل"}
            </Button>
          </form>
        </section>
      </main>
    </div>
  );
}
